/**
 * Performance utilities for caching formatter results and expensive operations
 * Functional approach with type-safe caching mechanisms
 */

import { isObjectType } from 'remeda'

// Type definitions for cache operations with improved TS compiler performance
type JSONPrimitive = string | number | boolean | null
type JSONObject = { [key: string]: JSONSerializable }
type JSONArray = JSONSerializable[]

type JSONSerializable = JSONPrimitive | JSONObject | JSONArray

/**
 * WeakMap-based cache for JSON strings to avoid repeated serialization
 * Uses WeakMap to allow garbage collection when objects are no longer referenced
 *
 * Performance benefits:
 * - Avoids repeated JSON.stringify calls during scrolling
 * - Automatic memory cleanup through garbage collection
 * - Zero memory leaks for ephemeral objects
 */
const jsonStringCache = new WeakMap<object, string>()

/**
 * Pure function to determine if value requires caching
 * Primitives and null don't benefit from WeakMap caching
 */
const isPrimitive = (value: unknown): value is JSONSerializable =>
  !isObjectType(value) || value === null

/**
 * Cached JSON.stringify with proper function overloads for type safety
 * Critical for performance when formatting cells during scrolling
 */
export function getCachedJsonString(
  obj: string | number | boolean | null
): string
export function getCachedJsonString(obj: object): string
export function getCachedJsonString(
  obj: string | number | boolean | null | object
): string {
  // Handle primitives directly - no caching needed
  if (isPrimitive(obj)) {
    return JSON.stringify(obj)
  }

  const cached = jsonStringCache.get(obj)

  if (cached !== undefined) {
    return cached
  }

  // Generate and cache new value
  const stringified = JSON.stringify(obj)
  jsonStringCache.set(obj, stringified)

  return stringified
}

// Export types for external use
export type { JSONSerializable }
